import Service, { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';
import { dropTask, restartableTask } from 'ember-concurrency';
import { query } from '@warp-drive/utilities/json-api';
import { SkipCache } from '@warp-drive/core-types/request';
import type { Store as StoreService } from '@warp-drive/core';
import type SessionContextService from 'tio-common/services/session-context';
import type { ReportsExtension as Report } from 'tio-data/data/resources/reports.ext';
import type { Company as CompanyModel } from 'tio-data/data/resources/company.type';
import { REPORT_TYPE_FILTER_VALUE, VALID_REPORT_TYPES } from '../utils/dataset-utils.ts';
import { updateReportRequest } from '../requests/report.ts';
import { companyIdFor } from '../utils/report-filtering.ts';

export interface CompanyOption {
  key: string;
  label: string;
}

/**
 * One page of reports; the API's default sort is updated_at desc, so these
 * are the most recently updated. mayHaveMore flags a full page so the UI
 * can say counts/search are bounded.
 */
export const REPORTS_PAGE_LIMIT = 200;

/**
 * One shared fetch of the admin reports catalog (reports + active companies)
 * for the Reports Home and Browse pages, so navigating between them doesn't
 * refetch. Archive/publish flows mutate report records in place, which keeps
 * this cached array coherent without a refresh. Only companies referenced by
 * the loaded reports are fetched (filter[id]), in the background after first
 * paint — see the load task.
 */
export default class ReportsCatalogService extends Service {
  @service declare store: StoreService;
  @service declare sessionContext: SessionContextService;

  @tracked reports: Report[] = [];
  @tracked companies: CompanyOption[] = [];
  /** True when the initial load failed; pages surface a retry hint. */
  @tracked loadFailed = false;

  @tracked private loaded = false;
  /** Company ids resolved (or confirmed absent) this session; a failed
   * fetch leaves its ids unmarked so the next load retries them. */
  #companyIdsFetched = new Set<string>();
  /** Owner the cached catalog belongs to — the singleton survives re-login
   * without app teardown, so an owner change drops the cache. */
  private ownerForLoad: string | null = null;

  get isLoading() {
    return !this.loaded && this.load.isRunning;
  }

  get hasLoaded() {
    return this.loaded;
  }

  /**
   * The report_type values this catalog surfaces. Reports show the
   * builder-supported product set; a subclass (dashboards) overrides this to
   * its own type. Kept overridable so both the server filter and the
   * client-side visibility gate stay derived from one place per kind.
   */
  protected get allowedReportTypes() {
    return VALID_REPORT_TYPES;
  }

  /**
   * CSV of report_type values for the listing's `filter[report_type]`
   * IN-query. Overridable so a subclass fetches a different slice (e.g.
   * dashboards) without touching the shared fetch machinery.
   */
  protected get reportTypeFilter() {
    return REPORT_TYPE_FILTER_VALUE;
  }

  /**
   * Reports the signed-in admin may see: an allowed report_type plus
   * private-ownership (private limited to its creator). Mirrors the shared
   * visibleToAdmin rule but reads the type set from allowedReportTypes so a
   * subclass can retarget the kind while keeping identical behavior here.
   */
  @cached
  get visibleReports() {
    const currentUserId = this.sessionContext.user?.id;
    const allowedTypes = this.allowedReportTypes;
    return this.reports.filter(
      (report) =>
        allowedTypes.has(report.reportType) &&
        (!report.isPrivate || String(report.createdBy) === String(currentUserId))
    );
  }

  @cached
  get companyNamesById() {
    return new Map(this.companies.map((c) => [c.key, c.label]));
  }

  /** True when the fetch filled a whole page — older reports may exist. */
  get mayHaveMore() {
    return this.reports.length >= REPORTS_PAGE_LIMIT;
  }

  /**
   * Stale-while-revalidate, scoped per owner: the first load populates the
   * catalog; later calls (each page construction) serve the cached data
   * instantly (isLoading stays false once loaded) while refetching in the
   * same task, so reports created/edited in the builder appear on return
   * without a full reload. A failed revalidation keeps the stale data
   * silently. An owner change (re-login without app teardown) drops the
   * previous admin's cache first; restartable so a new owner's load cancels
   * an in-flight one instead of being dropped.
   */
  load = restartableTask(async () => {
    const ownerId = this.sessionContext.user?.id ?? null;
    if (this.ownerForLoad !== ownerId) {
      this.reports = [];
      this.companies = [];
      this.loaded = false;
      this.#companyIdsFetched.clear();
      this.loadFailed = false;
      this.ownerForLoad = ownerId;
    }
    const wasLoaded = this.loaded;
    try {
      // Reports revalidate on every page entry (builder saves must show up).
      const reports = await this.#fetchReports();
      // Discard responses started for a different owner. Compare the LIVE
      // session id — ownerForLoad only changes via another load.perform(),
      // so it can't detect a re-login that happened with no competing load.
      if (!this.#ownerIsCurrent(ownerId)) return;
      this.reports = reports;
      this.loadFailed = false;
      this.loaded = true;
    } catch (error) {
      console.error('Failed to load the reports catalog:', error);
      if (!wasLoaded && this.#ownerIsCurrent(ownerId)) this.loadFailed = true;
      return;
    }
    // Companies resolve AFTER reports so only the ids the loaded reports
    // actually reference are fetched (filter[id]) — the full active-company
    // catalog took seconds against prod and was mostly never rendered. This
    // never gates first paint: labels, chips, and rail facets are getter-
    // driven and fill in reactively when it lands.
    const referenced = new Set<string>();
    for (const report of this.reports) {
      const id = companyIdFor(report);
      if (id) referenced.add(id);
    }
    const missing = [...referenced].filter((id) => !this.#companyIdsFetched.has(id));
    if (missing.length === 0) return;
    try {
      const fetched = await this.#fetchCompaniesByIds(missing);
      if (!this.#ownerIsCurrent(ownerId)) return;
      // Requested-but-absent ids (deleted / inaccessible companies) are
      // marked too so they don't refetch on every load; their rows show "—".
      for (const id of missing) this.#companyIdsFetched.add(id);
      const byKey = new Map(this.companies.map((c) => [c.key, c]));
      for (const company of fetched) byKey.set(company.key, company);
      // Alphabetical for the rail/chips (the old catalog was server-sorted).
      this.companies = [...byKey.values()].sort((a, b) => a.label.localeCompare(b.label));
    } catch (error) {
      // Non-fatal: company names stay unresolved ("—") and the next
      // catalog load retries (the failed ids stay unmarked).
      console.error('Failed to load companies for the reports catalog:', error);
    }
  });

  #ownerIsCurrent(ownerId: string | null | undefined) {
    return (
      this.ownerForLoad === ownerId && (this.sessionContext.user?.id ?? null) === (ownerId ?? null)
    );
  }

  refresh = dropTask(async () => {
    this.loaded = false;
    this.#companyIdsFetched.clear();
    await this.load.perform();
  });

  companyNameFor = (report: Report) => {
    const companyId = companyIdFor(report);
    if (!companyId) return undefined;
    return this.companyNamesById.get(companyId);
  };

  /**
   * Set/clear metadata.archivedAt; resolves true on success. Only the
   * camelCase `metadata` field is written back onto the record (the PATCH
   * response echoes what we sent); assigning raw response attributes would
   * hit the SchemaRecord set trap with dasherized keys. Failures log, leave
   * the record unchanged, and resolve false so callers can surface them.
   */
  archiveReport = async (report: Report) => {
    return this.#updateArchivedAt(report, new Date().toISOString());
  };

  unarchiveReport = async (report: Report) => {
    return this.#updateArchivedAt(report, undefined);
  };

  async #updateArchivedAt(report: Report, archivedAt: string | undefined) {
    if (!report?.id) return false;
    try {
      // Serialize behind any in-flight revalidation: its GET captured
      // pre-mutation metadata and would materialize AFTER our PATCH,
      // reverting the row to its old rail.
      if (this.load.isRunning) {
        await this.load.last?.catch(() => {});
      }
      const metadata = { ...(report.metadata || {}) };
      if (archivedAt) {
        metadata.archivedAt = archivedAt;
      } else {
        delete metadata['archivedAt'];
      }
      const request = updateReportRequest(report.id, { metadata });
      request.cacheOptions = { [SkipCache]: true };
      await this.store.request(request);
      report.metadata = metadata;
      // Converge with server truth — also corrects any read that slipped in
      // between the wait above and the PATCH landing.
      void this.load.perform();
      return true;
    } catch (error) {
      console.error(`Failed to ${archivedAt ? 'archive' : 'unarchive'} report:`, error);
      return false;
    }
  }

  async #fetchReports() {
    // reload (NOT SkipCache): revalidation must hit the network — the
    // document cache's 5-minute soft TTL would otherwise serve the pre-
    // builder-save list — while keeping content.data materialized records.
    const { content } = (await this.store.request({
      ...query<Report>(
        'report',
        {
          'page[limit]': REPORTS_PAGE_LIMIT,
          'filter[report_type]': this.reportTypeFilter,
        },
        { namespace: 'api/v2' }
      ),
      cacheOptions: { reload: true },
    })) as { content: { data: Report[] } };
    return content.data;
  }

  async #fetchCompaniesByIds(ids: string[]) {
    // Batched filter[id] lookups keep URLs bounded; page[limit] is pinned to
    // the batch size so a small server default can't truncate a response.
    // No is-active filter: a report scoped to a deactivated company should
    // still label its rows. Plain cache path — an identical id set within
    // the document-cache TTL is served without a network round trip.
    const BATCH = 100;
    const chunks: string[][] = [];
    for (let i = 0; i < ids.length; i += BATCH) {
      chunks.push(ids.slice(i, i + BATCH));
    }
    const pages = await Promise.all(
      chunks.map(async (chunk) => {
        const { content } = (await this.store.request(
          query<CompanyModel>('company', {
            'filter[id]': chunk.join(','),
            'page[limit]': BATCH,
          })
        )) as { content: { data: CompanyModel[] } };
        return content.data;
      })
    );
    return pages.flat().map((c) => ({
      key: String(c.id),
      label: c.name || `Company ${c.id}`,
    }));
  }
}
